import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogoUpim, BotonUpim } from '../ui-upim';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import {
  Menu,
  User,
  LogOut,
  LayoutDashboard,
  Settings,
  ChevronDown,
  Facebook,
  Instagram,
  Twitter,
  Mail,
  Phone,
  Award,
  Star,
  Home,
  Handshake,
  Globe,
  Bot,
  CreditCard,
  TrendingUp,
  Bell,
  Building2
} from 'lucide-react';

const navLinks = [
  { name: 'Inicio', path: '/' },
  { name: '¿Quiénes Somos?', path: '/nosotros' },
  { name: 'Beneficios', path: '/beneficios' },
  { name: 'Proveedores', path: '/proveedores' },
  { name: 'Calendario', path: '/calendario' },
  {
    name: 'Formación',
    path: '/academia',
    submenu: [
      { name: 'Academia UPIM', path: '/academia' },
      { name: 'Webinars', path: '/webinars' },
      { name: 'Certificaciones SEP', path: '/certificaciones' },
    ]
  },
  {
    name: 'UPIM IA',
    path: '/upim-ia',
    submenu: [
      { name: 'Asesorías con IA', path: '/asesorias' },
      { name: 'Chat UPIM IA', path: '/upim-ia/chat' },
      { name: 'Directorio Liderazgo', path: '/directorio' },
      { name: 'Postúlate como Líder', path: '/postulacion-liderazgo' },
    ]
  },
  { name: 'Recursos', path: '/recursos' },
  { name: 'Contacto', path: '/contacto' },
];

const crmMenuItems = [
  { name: 'Dashboard CRM', path: '/crm', icon: LayoutDashboard },
  { name: 'Mis Propiedades', path: '/crm/propiedades', icon: Home },
  { name: 'Alianzas', path: '/crm/alianzas', icon: Handshake },
  { name: 'Mi Micrositio', path: '/crm/micrositio', icon: Globe },
  { name: 'Bot IA Clientes', path: '/crm/bot-clientes', icon: Bot },
  { name: 'Credencial Digital', path: '/crm/credencial-digital', icon: CreditCard },
  { name: 'Reportes de Mercado', path: '/crm/reportes-mercado', icon: TrendingUp },
  { name: 'Alertas', path: '/crm/alertas', icon: Bell },
];

const redes = [
  { href: 'https://www.instagram.com/upimnacional/', icon: Instagram, label: 'Instagram' },
  { href: 'https://twitter.com/upimnacional', icon: Twitter, label: 'Twitter' },
  { href: 'https://www.facebook.com/upimnacional', icon: Facebook, label: 'Facebook' },
];

const claseEnlace = (activo) =>
  `relative whitespace-nowrap px-3.5 py-2 text-sm font-medium rounded-full transition-[color,background-color] duration-200 ${
    activo
      ? 'text-[var(--upim-texto)] bg-[var(--upim-superficie-3)]'
      : 'text-[var(--upim-texto-2)] hover:text-[var(--upim-texto)] hover:bg-[var(--upim-superficie-2)]'
  }`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardPath = () => {
    if (!user) return '/dashboard';
    switch (user.role) {
      case 'admin':
      case 'superadmin':
        return '/admin';
      case 'provider':
        return '/proveedor';
      default:
        return '/dashboard';
    }
  };

  return (
    <>
      {/* Franja institucional */}
      <div className="hidden lg:block bg-black border-b border-[var(--upim-borde)]" data-testid="navbar-franja">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-2 text-xs sm:px-8 lg:px-12">
          <div className="flex items-center gap-6">
            <a href="mailto:info@upim.org" className="flex items-center gap-2 text-[var(--upim-texto-3)] transition-colors hover:text-[var(--upim-acento)]">
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
              <span>info@upim.org</span>
            </a>
            <a href="tel:5571164745" className="flex items-center gap-2 text-[var(--upim-texto-3)] transition-colors hover:text-[var(--upim-acento)]">
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              <span>55 7116 4745</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="tracking-[0.22em] text-[10px] uppercase text-[var(--upim-texto-3)]">
              Unión de Profesionales Inmobiliarios de México
            </span>
            <span className="h-3 w-px bg-[var(--upim-borde-fuerte)]" />
            {redes.map(({ href, icon: Icono, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="text-[var(--upim-texto-3)] transition-colors hover:text-[var(--upim-acento)]"
              >
                <Icono className="h-4 w-4" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Navegación principal */}
      <nav className="upim-vidrio sticky top-0 z-50" data-testid="navbar-principal">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className="flex min-h-[76px] flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3">
            <Link to="/" className="flex flex-shrink-0 items-center" data-testid="navbar-logo-enlace" aria-label="Ir al inicio">
              <LogoUpim className="h-11 lg:h-12" claseTexto="text-xl" testId="navbar-logo" />
            </Link>

            <div className="order-3 hidden w-full items-center justify-center gap-0.5 lg:flex">
              {navLinks.map((link) => (
                link.submenu ? (
                  <DropdownMenu key={link.path}>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`navbar-menu-${link.path.replace(/\//g, '')}`}
                        className={`${claseEnlace(location.pathname === link.path)} flex items-center gap-1`}
                      >
                        {link.name}
                        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-52">
                      {link.submenu.map((sublink) => (
                        <DropdownMenuItem
                          key={sublink.path}
                          onClick={() => navigate(sublink.path)}
                          className={location.pathname === sublink.path ? 'text-[var(--upim-acento)]' : ''}
                        >
                          {sublink.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`navbar-enlace-${link.path === '/' ? 'inicio' : link.path.replace(/\//g, '')}`}
                    className={claseEnlace(location.pathname === link.path)}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            <div className="hidden flex-shrink-0 items-center gap-3 lg:flex">
              {isAuthenticated ? (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid="navbar-crm-boton"
                        className="inline-flex items-center gap-2 rounded-full border border-[var(--upim-borde-fuerte)] px-4 py-2 text-sm font-medium text-[var(--upim-texto)] transition-colors hover:border-[var(--upim-acento)] hover:text-[var(--upim-acento)]"
                      >
                        <Building2 className="h-4 w-4" aria-hidden="true" />
                        CRM
                        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60">
                      {crmMenuItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={location.pathname === item.path ? 'text-[var(--upim-acento)]' : ''}
                          >
                            <Icon className="mr-2 h-4 w-4 text-[var(--upim-acento)]" aria-hidden="true" />
                            {item.name}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid="navbar-usuario-boton" className="flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-3 transition-colors hover:bg-[var(--upim-superficie-2)]">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--upim-acento-suave)] text-[var(--upim-acento)]">
                          <User className="h-4.5 w-4.5" aria-hidden="true" />
                        </span>
                        <span className="text-sm font-medium text-[var(--upim-texto)]">{user?.name?.split(' ')[0]}</span>
                        <ChevronDown className="h-4 w-4 text-[var(--upim-texto-3)]" aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60">
                      <div className="px-3 py-2">
                        <p className="text-sm font-medium text-[var(--upim-texto)]">{user?.name}</p>
                        <p className="text-xs text-[var(--upim-texto-3)]">{user?.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate(getDashboardPath())}>
                        <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden="true" />
                        Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/mis-constancias')}>
                        <Award className="mr-2 h-4 w-4" aria-hidden="true" />
                        Mis Constancias
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/mis-insignias')}>
                        <Star className="mr-2 h-4 w-4" aria-hidden="true" />
                        Compartir Insignias
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/perfil')}>
                        <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
                        Mi Perfil
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} data-testid="navbar-cerrar-sesion" className="text-[var(--u-red)]">
                        <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                        Cerrar Sesión
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <BotonUpim
                    variante="fantasma"
                    testId="navbar-iniciar-sesion"
                    onClick={() => navigate('/login')}
                    className="px-5 py-2.5"
                  >
                    Iniciar Sesión
                  </BotonUpim>
                  <BotonUpim
                    variante="solido"
                    testId="navbar-afiliate"
                    onClick={() => navigate('/afiliacion')}
                    className="px-5 py-2.5"
                  >
                    Afíliate
                  </BotonUpim>
                </>
              )}
            </div>

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="navbar-menu-movil" aria-label="Abrir menú">
                  <Menu className="h-6 w-6 text-[var(--upim-texto)]" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] border-l border-[var(--upim-borde)] bg-[var(--upim-superficie-1)] p-0 sm:w-[360px]">
                <div className="flex h-full flex-col">
                  <SheetTitle className="sr-only">Menú de navegación UPIM</SheetTitle>
                  <div className="border-b border-[var(--upim-borde)] p-5">
                    <LogoUpim className="h-10" claseTexto="text-lg" testId="navbar-logo-movil" />
                  </div>

                  <div className="flex-1 overflow-y-auto py-5">
                    <div className="space-y-1 px-3">
                      {navLinks.map((link) => (
                        link.submenu ? (
                          <div key={link.path} className="space-y-1 pt-2">
                            <p className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--upim-acento)]">
                              {link.name}
                            </p>
                            {link.submenu.map((sublink) => (
                              <Link
                                key={sublink.path}
                                to={sublink.path}
                                onClick={() => setIsOpen(false)}
                                className={`ml-2 flex items-center rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                                  location.pathname === sublink.path
                                    ? 'bg-[var(--upim-superficie-3)] text-[var(--upim-texto)]'
                                    : 'text-[var(--upim-texto-2)] hover:bg-[var(--upim-superficie-2)] hover:text-[var(--upim-texto)]'
                                }`}
                              >
                                {sublink.name}
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                              location.pathname === link.path
                                ? 'bg-[var(--upim-superficie-3)] text-[var(--upim-texto)]'
                                : 'text-[var(--upim-texto-2)] hover:bg-[var(--upim-superficie-2)] hover:text-[var(--upim-texto)]'
                            }`}
                          >
                            {link.name}
                          </Link>
                        )
                      ))}
                    </div>

                    <div className="mt-6 border-t border-[var(--upim-borde)] px-7 pt-6">
                      <p className="mb-3 text-[10px] uppercase tracking-[0.22em] text-[var(--upim-texto-3)]">Síguenos</p>
                      <div className="flex gap-5">
                        {redes.map(({ href, icon: Icono, label }) => (
                          <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="text-[var(--upim-texto-3)] transition-colors hover:text-[var(--upim-acento)]">
                            <Icono className="h-5 w-5" aria-hidden="true" />
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 border-t border-[var(--upim-borde)] p-5">
                    {isAuthenticated ? (
                      <>
                        <BotonUpim
                          variante="contorno"
                          className="w-full"
                          onClick={() => { navigate(getDashboardPath()); setIsOpen(false); }}
                        >
                          <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                          Dashboard
                        </BotonUpim>
                        <BotonUpim
                          variante="fantasma"
                          className="w-full text-[var(--u-red)]"
                          onClick={() => { handleLogout(); setIsOpen(false); }}
                        >
                          <LogOut className="h-4 w-4" aria-hidden="true" />
                          Cerrar Sesión
                        </BotonUpim>
                      </>
                    ) : (
                      <>
                        <BotonUpim variante="contorno" className="w-full" onClick={() => { navigate('/login'); setIsOpen(false); }}>
                          Iniciar Sesión
                        </BotonUpim>
                        <BotonUpim variante="solido" className="w-full" onClick={() => { navigate('/afiliacion'); setIsOpen(false); }}>
                          Afíliate
                        </BotonUpim>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
